import React, { useLayoutEffect, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { Badge, BADGE_SHAPES } from "@widgets/datadisplay/badge/index.jsx";
import { HorizontalScroller } from "@widgets/navigation/horizontalscroller/index.jsx";

import styles from "./scrollabletab.css";

import LeftArrowIcon from "./icons/LeftArrow.svg";
import RightArrowIcon from "./icons/RightArrow.svg";

export const SCROLLABLE_TABS_SIZES = {
  SMALL: "small",
  MEDIUM: "medium"
};

export const SCROLLABLE_TABS_HIGHLIGHT_TYPES = {
  PRIMARY: "primary",
  GRAY: "gray"
};

export function ScrollableTabs({
  children,
  activeTabId,
  onChange,
  className,
  highlight = SCROLLABLE_TABS_HIGHLIGHT_TYPES.PRIMARY,
  size = SCROLLABLE_TABS_SIZES.MEDIUM
}) {
  const rootRef = useRef(null);
  const highlightRef = useRef(null);
  const leftNavRef = useRef(null);
  const rightNavRef = useRef(null);

  const slots = React.Children.map(children, (child) => child);

  const tabItems = slots.filter((child) => child.type === ScrollableTabs.Tab);

  const leftAction = slots.find(
    (child) => child.type === ScrollableTabs.LeftAction
  );

  const rightAction = slots.find(
    (child) => child.type === ScrollableTabs.RightAction
  );

  function highlightTab() {
    setTimeout(() => {
      const activeTabEle =
        rootRef.current &&
        rootRef.current.querySelector(`[data-active="true"]`);
      if (activeTabEle) {
        highlightRef.current.style.display = "block";
        highlightRef.current.style.width = `${Math.floor(
          activeTabEle.offsetWidth - 1
        )}px`;
        if (window.isRTL) {
          highlightRef.current.style.left = "auto";
          highlightRef.current.style.right = `${Math.floor(
            activeTabEle.parentElement.offsetWidth -
              activeTabEle.offsetLeft -
              activeTabEle.offsetWidth
          )}px`;
        } else {
          highlightRef.current.style.right = "auto";
          highlightRef.current.style.left = `${Math.floor(
            activeTabEle.offsetLeft
          )}px`;
        }
      } else if (highlightRef.current) {
        highlightRef.current.style.display = "none";
      }
    });
  }

  useLayoutEffect(function highlightActiveTab() {
    const mutationObserver = new MutationObserver(() => {
      highlightTab();
    });
    mutationObserver.observe(rootRef.current, {
      attributes: true,
      childList: true,
      subtree: true
    });
    highlightTab();
    return () => mutationObserver.disconnect();
  }, []);

  useLayoutEffect(
    function activeTabChanges() {
      if (activeTabId) {
        requestAnimationFrame(() => {
          const activeTabEle =
            rootRef.current &&
            rootRef.current.querySelector(`[data-tab-id="${activeTabId}"]`);
          highlightTab();
          activeTabEle && activeTabEle.scrollIntoView({});
        });
      }
    },
    [activeTabId, size.width]
  );

  return (
    <div
      className={`${styles.tabs} ${className} ${styles[size]} ${
        styles[`highlight${highlight}`]
      }`}
      ref={rootRef}
    >
      {leftAction && (
        <div className={`${styles.tabaction} ${styles.tableftaction}`}>
          {leftAction}
        </div>
      )}
      <HorizontalScroller
        className={styles.tablist}
        leftNavRenderer={({ visible, disabled, onNav }) => {
          return (
            visible && (
              <button
                ref={leftNavRef}
                className={`${styles.tabnav} ${styles.tableftnav} ${
                  disabled ? styles.disabled : ""
                }`}
                tabIndex="-1"
                onClick={onNav}
              >
                <LeftArrowIcon />
              </button>
            )
          );
        }}
        rightNavRenderer={({ visible, disabled, onNav }) => {
          return (
            visible && (
              <button
                ref={rightNavRef}
                className={`${styles.tabnav} ${styles.tabrightnav} ${
                  disabled ? styles.disabled : ""
                }`}
                tabIndex="-1"
                onClick={onNav}
              >
                <RightArrowIcon />
              </button>
            )
          );
        }}
      >
        {tabItems.map((child, i) => {
          const {
            tabId,
            icon,
            title,
            number,
            maxNumber,
            badgeColor,
            badgeShape
          } = child.props;
          const isActive = tabId === activeTabId;
          return (
            <button
              data-tab-id={tabId}
              data-active={isActive}
              className={`${styles.tab} ${isActive ? styles.active : ""}`}
              key={i}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange && onChange(tabId);
              }}
            >
              {icon && <div className={styles.tabicon}>{icon}</div>}
              {title}
              {number > 0 && (
                <div
                  className={`${styles.badge} ${styles[badgeShape]} ${badgeShape}`}
                >
                  <Badge
                    color={badgeColor}
                    shape={badgeShape || BADGE_SHAPES.BOX}
                    count={number}
                    maxCount={maxNumber}
                  />
                </div>
              )}
            </button>
          );
        })}
        <div className={styles.highlight} ref={highlightRef} />
      </HorizontalScroller>
      {rightAction && (
        <div className={`${styles.tabaction} ${styles.tabrightaction}`}>
          {rightAction}
        </div>
      )}
    </div>
  );
}

ScrollableTabs.defaultProps = {
  highlight: SCROLLABLE_TABS_HIGHLIGHT_TYPES.PRIMARY,
  size: SCROLLABLE_TABS_SIZES.MEDIUM
};

ScrollableTabs.propTypes = {
  /** A list of Tabs components. */
  children: PropTypes.node.isRequired,
  /** A callback function that gets invoked when the tab is changed. */
  onChange: PropTypes.func.isRequired,
  /** ID of the currently opened Tab. */
  activeTabId: PropTypes.string.isRequired,
  /** To override the existing styles of the component */
  className: PropTypes.string,
  /** Size property is used to defined the size of the tab as medium and small. */
  size: PropTypes.oneOf(Object.values(SCROLLABLE_TABS_SIZES)),
  /** The color of the underline of the action Tab. (Gray or Primary) */
  highlight: PropTypes.oneOf(Object.values(SCROLLABLE_TABS_HIGHLIGHT_TYPES))
};

function Tab({ children }) {
  return <>{children}</>;
}

Tab.propTypes = {
  /** A component or html that needs to be substituted inside of a tab. */
  children: PropTypes.node.isRequired
};

ScrollableTabs.Tab = Tab;

function LeftAction({ children }) {
  return <>{children}</>;
}

LeftAction.propTypes = {
  children: PropTypes.node.isRequired
};

ScrollableTabs.LeftAction = LeftAction;

function RightAction({ children }) {
  return <>{children}</>;
}

RightAction.propTypes = {
  children: PropTypes.node.isRequired
};

ScrollableTabs.RightAction = RightAction;
